import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from '@mui/styles';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import FormatColorResetOutlinedIcon from '@mui/icons-material/FormatColorResetOutlined';

import useStore from 'utils/ieltsStore';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: 'relative',
  },
  toolbar: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
  },
  markerActive: {
    color: theme.palette.primary.main,
    backgroundColor: '#fff3a3',
  },
  text: {
    padding: theme.spacing(0, 5, 2, 5),
  },
  marking: {
    cursor: 'cell',
  },
}));

// Stable id per passage content (djb2), so highlights re-attach to the same passage.
function hashString(str = '') {
  let hash = 5381;
  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return `p${hash >>> 0}`;
}

// Build a DOM range from plain-text [start, end] offsets within root.
function rangeFromOffsets(root, start, end) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let total = 0;
  let startNode = null;
  let startOff = 0;
  let endNode = null;
  let endOff = 0;
  let node;

  while ((node = walker.nextNode())) {
    const len = node.nodeValue.length;
    if (startNode === null && start <= total + len) {
      startNode = node;
      startOff = start - total;
    }
    if (end <= total + len) {
      endNode = node;
      endOff = end - total;
      break;
    }
    total += len;
  }

  if (!startNode || !endNode) return null;

  try {
    const range = document.createRange();
    range.setStart(startNode, startOff);
    range.setEnd(endNode, endOff);
    return range;
  } catch (e) {
    return null;
  }
}

// Wrap every text-node slice inside the range in <mark class="passage-hl">.
// Works across tags because each slice is a sub-range of a single text node,
// for which surroundContents is always safe.
function wrapRange(root, range) {
  if (!range || range.collapsed) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const nodes = [];
  let node;
  while ((node = walker.nextNode())) {
    if (range.intersectsNode(node)) nodes.push(node);
  }

  nodes.forEach((textNode) => {
    if (textNode.parentElement && textNode.parentElement.closest('.passage-hl')) return;

    let from = 0;
    let to = textNode.nodeValue.length;
    if (textNode === range.startContainer) from = range.startOffset;
    if (textNode === range.endContainer) to = range.endOffset;
    if (from >= to) return;

    const slice = document.createRange();
    slice.setStart(textNode, from);
    slice.setEnd(textNode, to);

    const mark = document.createElement('mark');
    mark.className = 'passage-hl';
    try {
      slice.surroundContents(mark);
    } catch (e) {
      /* skip slices that can't be wrapped */
    }
  });
}

function unwrap(mark) {
  const parent = mark.parentNode;
  if (!parent) return;
  while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
  parent.removeChild(mark);
  parent.normalize();
}

// Read current highlights from the DOM as plain-text [{ start, end }] offsets.
function serialize(root) {
  const result = [];
  root.querySelectorAll('.passage-hl').forEach((mark) => {
    const before = document.createRange();
    before.selectNodeContents(root);
    before.setEndBefore(mark);
    const start = before.toString().length;
    result.push({ start, end: start + (mark.textContent || '').length });
  });
  return result;
}

function HighlightablePassage({ html = '', title, variant = 'body01-medium' }) {
  const classes = useStyles();
  const ref = React.useRef(null);
  const passageId = React.useMemo(() => hashString(html), [html]);

  const markerOn = useStore((state) => state.markerOn);
  const toggleMarker = useStore((state) => state.toggleMarker);
  const setPassageHighlights = useStore((state) => state.setPassageHighlights);

  // (Re)apply persisted highlights whenever the passage content changes.
  // React resets the innerHTML on content change (wiping marks), then this runs.
  React.useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const list = useStore.getState().highlights?.[passageId] || [];
    list.forEach(({ start, end }) => wrapRange(root, rangeFromOffsets(root, start, end)));
  }, [html, passageId]);

  const save = () => {
    if (ref.current) setPassageHighlights(passageId, serialize(ref.current));
  };

  const handleMouseUp = () => {
    if (!markerOn) return;
    const root = ref.current;
    const selection = window.getSelection();
    if (!root || !selection || selection.isCollapsed || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return;

    wrapRange(root, range);
    selection.removeAllRanges();
    save();
  };

  const handleClick = (e) => {
    if (!markerOn) return;
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) return; // drag-select -> handled by mouseup
    const mark = e.target.closest && e.target.closest('.passage-hl');
    if (mark) {
      unwrap(mark);
      save();
    }
  };

  const clearAll = () => {
    const root = ref.current;
    if (!root) return;
    root.querySelectorAll('.passage-hl').forEach(unwrap);
    save();
  };

  return (
    <Box className={classes.wrapper} title={title}>
      <Box className={classes.toolbar}>
        <Tooltip
          title={markerOn
            ? 'Highlighter on — select text to mark, click a mark to erase'
            : 'Highlighter'}
        >
          <IconButton
            size="small"
            onClick={toggleMarker}
            className={markerOn ? classes.markerActive : ''}
          >
            <BorderColorOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Clear all highlights">
          <IconButton size="small" onClick={clearAll}>
            <FormatColorResetOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography
        ref={ref}
        component="div"
        variant={variant}
        className={`${classes.text} allow-text-select ${markerOn ? classes.marking : ''}`}
        onMouseUp={handleMouseUp}
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </Box>
  );
}

export default HighlightablePassage;
